import * as Scenes from "oc-threejs/Scenes";
import { PColor } from "../properties/PColor";
import { PNumber } from "../properties/PNumber";
import { PVector3 } from "../properties/PVector3";
import { PEuler } from "../properties/PEuler";
import { PBoolean } from "../properties/PBoolean";
import { RectAreaLightUniformsLib } from "three/examples/jsm/lights/RectAreaLightUniformsLib";
import { json, MetadataRow } from "../../global";
import * as Maths3 from "oc-threejs/Maths3";
import { v3 } from "oc-threejs/short3";
import { PNodeListMeta } from "../properties/PNodeListMeta";
import { LightJson } from "./LightJson";
import { show } from "oc-ui/Doms";
import { AbstractMetaJsonNode } from "./AbstractMetaJsonNode";
import { PAny } from "../properties/PAny";
import { PString } from "../properties/PString";
import { PropertyOrKeyValue } from "../Node";

export interface FakeLightPlaneProps {
  name: string;
  type: "Glow" | "GlowDown";
  intensity: number;
  fakeLightColor: string;
  falloffIntensity: number;
  edgeMargin: number;
  fuzziness: number;
  seiteVorne: boolean;
  seiteHinten: boolean;
  seiteLinks: boolean;
  seiteRechts: boolean;
  seiteUnten: boolean;
  sizeOffset: number[];
  centerOffset: number[];
  neigung: number;
  visible: boolean;
}

export const fakeLightDefaultProps = {
  type: "Glow",
  intensity: 3.0,
  fakeLightColor: "#ffffff",
  falloffIntensity: 0.2,
  edgeMargin: 0.03,
  fuzziness: 0.001,
  seiteVorne: true,
  seiteHinten: true,
  seiteLinks: true,
  seiteRechts: true,
  seiteUnten: false,
  sizeOffset: [0, 0, 0],
  centerOffset: [0, 0, 0],
  neigung: 0,
};

export class ModelLightJson extends AbstractMetaJsonNode implements FakeLightPlaneProps {
  updateSceneCalled = false;

  type: "Glow" | "GlowDown";
  intensity: number;
  fakeLightColor: string;
  falloffIntensity: number;
  edgeMargin: number;
  fuzziness: number;
  seiteVorne: boolean;
  seiteHinten: boolean;
  seiteLinks: boolean;
  seiteRechts: boolean;
  seiteUnten: boolean;
  sizeOffset: number[];
  centerOffset: number[];
  neigung: number;
  visible: boolean;

  updateScene() {
    this.updateSceneCalled = true;
    this._dataChanged = {};
    if (!this.changed("json", () => this.toJSON())) {
      return;
    }

    const { metadata } = APP.richMetadata;
    const entry = metadata.ModelLightJson.find((m) => m.id == this.id);
    const json = this.toJSON();
    entry.Json = json;

    let fakeLightsHolders = Scenes.filter(STAGE.scene, (c) => (c as any).updateFakeLight);
    fakeLightsHolders.forEach((c) => (c as any).updateFakeLight(json));
    STAGE.dirty();
  }

  createProperties() {
    return [
      new PAny({
        name: "id",
        type: "Id",
        node: this,
        showInEditor: (p) => false,
      }),
      new PString({
        name: "name",
        primaryKey: true,
        comment: "Interner eindeutiger Primärschlüssel",
        node: this,
      }),
      new PString({
        name: "type",
        selectOptions: ["Glow", "GlowDown"],
        node: this,
      }),
      new PNumber({
        name: "intensity",
        min: 0,
        max: 10,
        default: 1,
        comment:
          "The light's intensity, or strength. In physically correct mode, the units of intensity depend on the type of light. Default is 1.0.",
        node: this,
      }),
      new PColor({
        name: "fakeLightColor",
        default: "#ffffff",
        comment: "Farbe des Fake-Lichts",
        node: this,
      }),
      new PNumber({
        name: "falloffIntensity",
        min: 0,
        max: 0.1,
        default: 0.03,
        comment: "Wie schnell der Lichtkegel verblasst",
        node: this,
      }),
      new PNumber({
        name: "edgeMargin",
        min: 0,
        max: 0.1,
        default: 0.03,
        comment:
          "Wie weit vom Rand der Leuchte der Lichtkegel endet (Greift nur bei rechteckigen Leuchten)",
        node: this,
      }),
      new PNumber({
        name: "fuzziness",
        min: 0,
        max: 0.05,
        default: 0.001,
        comment: "Rauschanteil des Lichtkegels am Rand",
        node: this,
      }),
      new PBoolean({
        name: "seiteVorne",
        comment:
          "soll eine FakeLight-Ebene auf der entsprechende Seite der Boundingbox des Basisobjektes angezeigt werden?",
        default: true,
        node: this,
      }),
      new PBoolean({
        name: "seiteHinten",
        comment:
          "soll eine FakeLight-Ebene auf der entsprechende Seite der Boundingbox des Basisobjektes angezeigt werden?",
        default: true,
        node: this,
      }),
      new PBoolean({
        name: "seiteLinks",
        comment:
          "soll eine FakeLight-Ebene auf der entsprechende Seite der Boundingbox des Basisobjektes angezeigt werden?",
        default: true,
        node: this,
      }),
      new PBoolean({
        name: "seiteRechts",
        comment:
          "soll eine FakeLight-Ebene auf der entsprechende Seite der Boundingbox des Basisobjektes angezeigt werden?",
        default: true,
        node: this,
      }),
      new PBoolean({
        name: "seiteUnten",
        comment:
          "soll eine FakeLight-Ebene auf der entsprechende Seite der Boundingbox des Basisobjektes angezeigt werden?",
        default: false,
        node: this,
      }),
      new PVector3({
        name: "sizeOffset",
        comment: "Vergrößern der Box-Size",
        node: this,
      }),
      new PVector3({
        name: "centerOffset",
        comment: "Versatz des Box-Mittelpunktes",
        node: this,
      }),
      new PNumber({
        name: "neigung",
        comment: "Neigungswinkel der Licht-Ebene",
        min: -360,
        max: 360,
        default: 0,
        decimals: 1,
        node: this,
      }),
      new PBoolean({
        name: "visible",
        comment: "If set to false the light will be turned off",
        default: true,
        node: this,
      }),
    ] as PropertyOrKeyValue[];
  }

  getTableName() {
    return "ModelLightJson";
  }
}
